import type { ReactNode } from 'react';
import { Box, Flex, HStack, Stack, Text } from '@chakra-ui/react';
import { LuCheck, LuSparkles, LuX } from 'react-icons/lu';
import { useIntl } from 'react-intl';
import { Button } from '@/components/ui/button';

const AMBER = '#b3741b';
const AMBER_BG = 'rgba(179,116,27,0.10)';
const AMBER_TRACK = '#eadfc9';
const BLUE_SOFT = 'rgba(68,108,151,0.07)';
const BLUE_LINE = 'rgba(68,108,151,0.22)';

interface UpgradeDialogPanelProps {
    eyebrow: ReactNode;
    title: ReactNode;
    description: ReactNode;
    benefits: ReactNode[];
    cta: ReactNode;
    dismissAction: ReactNode;
    footer: ReactNode;
    onClose: () => void;
    onViewPlans: () => void;
    canViewPlans: boolean;
    limitMeter?: {
        label: ReactNode;
        resetLabel: ReactNode;
    };
}

export function UpgradeDialogPanel({
    eyebrow,
    title,
    description,
    benefits,
    cta,
    dismissAction,
    footer,
    onClose,
    onViewPlans,
    canViewPlans,
    limitMeter,
}: UpgradeDialogPanelProps) {
    const intl = useIntl();
    const closeLabel = intl.formatMessage({
        id: 'a0b1c2d3-1111-4a2b-8c3d-000000000912',
        defaultMessage: 'Close',
    });

    return (
        <Box
            w="480px"
            maxW="100%"
            bg="bg.panel"
            borderRadius="18px"
            overflow="hidden"
            boxShadow="0 30px 80px -20px rgba(20,16,10,0.55)"
            fontFamily="body"
            color="fg.default"
        >
            <Box padding="26px 28px 0">
                <Flex align="center" justify="space-between">
                    <Flex
                        w="44px"
                        h="44px"
                        borderRadius="12px"
                        bg={AMBER_BG}
                        color={AMBER}
                        align="center"
                        justify="center"
                        fontSize="22px"
                        lineHeight={1}
                    >
                        <LuSparkles />
                    </Flex>
                    <Box
                        as="button"
                        onClick={onClose}
                        bg="transparent"
                        border="none"
                        color="fg.subtle"
                        cursor="pointer"
                        padding="4px"
                        aria-label={closeLabel}
                    >
                        <LuX size={18} />
                    </Box>
                </Flex>
                <Text
                    fontSize="11.5px"
                    fontWeight={700}
                    color={AMBER}
                    textTransform="uppercase"
                    letterSpacing="0.6px"
                    mt={4}
                    mb={1.5}
                >
                    {eyebrow}
                </Text>
                <Text as="h3" fontSize="21px" fontWeight={800} letterSpacing="-0.4px" lineHeight={1.2} m={0}>
                    {title}
                </Text>
                <Text mt={2.5} fontSize="13.5px" color="fg.muted" lineHeight={1.55}>
                    {description}
                </Text>

                {limitMeter && (
                    <Box mt={4} mb={1}>
                        <Flex justify="space-between" align="baseline" mb={1.5}>
                            <Text fontSize="12.5px" color={AMBER} fontWeight={700}>
                                {limitMeter.label}
                            </Text>
                            <Text fontSize="12px" color="fg.subtle">
                                {limitMeter.resetLabel}
                            </Text>
                        </Flex>
                        <Box h="7px" borderRadius="4px" bg={AMBER_TRACK} overflow="hidden">
                            <Box w="100%" h="100%" bg={AMBER} borderRadius="4px" />
                        </Box>
                    </Box>
                )}
            </Box>

            <Box
                mt={5}
                padding="20px 28px 24px"
                bg={BLUE_SOFT}
                borderTopWidth="1px"
                borderColor={BLUE_LINE}
            >
                <Stack gap={2.5} mb={4}>
                    {benefits.map((benefit, index) => (
                        <HStack key={index} align="flex-start" gap={2.5}>
                            <Box color="brand.500" mt={0.5} fontSize="14px" lineHeight={1}>
                                <LuCheck />
                            </Box>
                            <Text fontSize="13px" fontWeight={600} color="fg.default" lineHeight={1.45}>
                                {benefit}
                            </Text>
                        </HStack>
                    ))}
                </Stack>
                <Button
                    w="full"
                    py={3.5}
                    borderRadius="10px"
                    fontSize="14.5px"
                    fontWeight={700}
                    onClick={onViewPlans}
                    disabled={!canViewPlans}
                >
                    {cta}
                </Button>
                <Flex justify="center" mt={3}>
                    <Box
                        as="button"
                        onClick={onClose}
                        bg="transparent"
                        border="none"
                        color="fg.muted"
                        fontFamily="inherit"
                        fontSize="13px"
                        fontWeight={600}
                        cursor="pointer"
                    >
                        {dismissAction}
                    </Box>
                </Flex>
                <Text textAlign="center" mt={2.5} fontSize="11.5px" color="fg.subtle">
                    {footer}
                </Text>
            </Box>
        </Box>
    );
}